import { v4 as uuidv4 } from 'uuid';

import config from '../config';
import { eventTracking } from '../util/api';
import { getAttribution } from '../util/consent/attribution';
import { getConsent, onConsentChange } from '../util/consent/consentState';
import hasEffectiveConsent from '../util/consent/effectiveConsent';
import { collectTrackingIds } from '../util/consent/trackingIds';
import { logError } from '../util/log';
import { initFacebookPixel, isFacebookPixelInitialized } from './facebookPixel';

export class FacebookBaseFacebookEvent {
  static name = '';
  static eventData = {};
  static _explicitEventID = false;
  static _sourceUrl = null;
  static params = {};
  static currentUserUUID = null;

  static setParam(key, value) {
    if (value != null) this.params[key] = value;
    return this;
  }

  static withCurrentUserUUID(uuid) {
    this.currentUserUUID = uuid || null;
    return this;
  }

  static withCustomName(customName) {
    this.name = customName;
    return this;
  }

  static withParams(params) {
    this.params = { ...this.params, ...params };
    return this;
  }

  // The relay's url (Meta CAPI event_source_url). Without it the relay reads
  // window.location.href once the awaited consent check and pixel import are
  // done, which may already be the next page.
  static withSourceUrl(sourceUrl) {
    this._sourceUrl = sourceUrl || null;
    return this;
  }

  static withEventID(eventID) {
    if (eventID) {
      this.eventData = { ...this.eventData, eventID };
      this._explicitEventID = true;
    }
    return this;
  }

  static withCurrency(currency) {
    return this.setParam('currency', currency);
  }

  static withValue(value) {
    return this.setParam('value', value);
  }

  static withContentCategory(contentCategory) {
    return this.setParam('content_category', contentCategory);
  }

  static withContentIds(contentIds) {
    return this.setParam('content_ids', contentIds);
  }

  static withContentName(contentName) {
    return this.setParam('content_name', contentName);
  }

  static withContents(contents) {
    return this.setParam('contents', contents);
  }

  static withNumItems(numItems) {
    return this.setParam('num_items', numItems);
  }

  static withContentType(contentType) {
    return this.setParam('content_type', contentType);
  }

  static withSearchString(searchString) {
    return this.setParam('search_string', searchString);
  }

  static async logEvent() {
    try {
      // Must be awaited: logFacebookEvent is async (permission check + dynamic
      // pixel import) — a fire-and-forget call let callers move on (and tests
      // assert) before the relay actually fired.
      await logFacebookEvent(this);
    } catch (e) {
      logError(e, 'facebook-event-tracking');
    }
  }

  static toJSON() {
    return {
      name: this.name,
      params: this.params,
      eventData: this.eventData,
    };
  }
}

export class FacebookScrollEvent extends FacebookBaseFacebookEvent {
  static name = 'Scroll';
}

export class FacebookPageViewEvent extends FacebookBaseFacebookEvent {
  static name = 'PageView';
  static deferUntilConsent = true;
}

export class FacebookAddPaymentInfoEvent extends FacebookBaseFacebookEvent {
  static name = 'AddPaymentInfo';
}

export class FacebookAddToWishlistEvent extends FacebookBaseFacebookEvent {
  static name = 'AddToWishlist';

  static withContentName(contentName) {
    return this.setParam('content_name', contentName);
  }

  static withContentCategory(contentCategory) {
    return this.setParam('content_category', contentCategory);
  }

  static withContentIds(contentIds) {
    return this.setParam('content_ids', contentIds);
  }

  static withContents(contents) {
    return this.setParam('contents', contents);
  }

  static withCurrency(currency) {
    return this.setParam('currency', currency);
  }

  static withValue(value) {
    return this.setParam('value', parseFloat(value));
  }
}

export class FacebookCompleteRegistrationEvent extends FacebookBaseFacebookEvent {
  static name = 'CompleteRegistration';
}

export class FacebookInitiateCheckoutEvent extends FacebookBaseFacebookEvent {
  static name = 'InitiateCheckout';

  static withContents(contents) {
    return this.setParam('contents', contents);
  }

  static withCurrency(currency) {
    return this.setParam('currency', currency);
  }

  static withValue(value) {
    return this.setParam('value', value);
  }
}

export class FacebookBookingRequestEvent extends FacebookBaseFacebookEvent {
  static name = 'BookingRequest';
}

export class FacebookPurchaseEvent extends FacebookBaseFacebookEvent {
  static name = 'Purchase';

  static withContents(contents) {
    return this.setParam('contents', contents);
  }

  static withCurrency(currency) {
    return this.setParam('currency', currency);
  }

  static withNumItems(numItems) {
    return this.setParam('num_items', numItems);
  }

  static withValue(value) {
    return this.setParam('value', value);
  }

  static async logEvent() {
    try {
      if (typeof this.params.value === 'undefined') {
        throw new Error('value is required');
      }
      if (!this.params.currency) {
        throw new Error('currency is required');
      }
      await super.logEvent();
    } catch (e) {
      logError(e, 'facebook-event-tracking');
    }
  }
}

export class FacebookSearchEvent extends FacebookBaseFacebookEvent {
  static name = 'Search';
  static deferUntilConsent = true;

  static withSearchString(searchString) {
    return this.setParam('search_string', searchString);
  }
}

export class FacebookViewContentEvent extends FacebookBaseFacebookEvent {
  static name = 'ViewContent';
  static deferUntilConsent = true;

  static withContentCategory(contentCategory) {
    return this.setParam('content_category', contentCategory);
  }

  static withContentName(contentName) {
    return this.setParam('content_name', contentName);
  }

  static withContentType(contentType) {
    return this.setParam('content_type', contentType);
  }

  static withContentIds(contentIds) {
    return this.setParam('content_ids', contentIds);
  }

  static withCurrency(currency) {
    return this.setParam('currency', currency);
  }

  static withValue(value) {
    return this.setParam('value', value);
  }
}

export class FacebookCustomEvent extends FacebookBaseFacebookEvent {
  static name = 'Custom';

  static withParams(params) {
    this.params = { ...this.params, ...params };
    return this;
  }

  static withCustomName(customName) {
    this.name = customName;
    return this;
  }
}

// params/eventData are static on FacebookBaseFacebookEvent and none of its ~11
// subclasses redeclare them, so ES statics are inherited (not copied) and every
// class shares the literal same objects. Without resetting after each fire,
// fields set by one event (e.g. ViewContent's contents/content_ids) leak into
// whatever unrelated event class fires next (e.g. Search, AddPaymentInfo). Must
// reset on the BASE class — `this.params = {}` from within a subclass only
// shadows the shared object for that one subclass, masking the bug rather than
// fixing it.
const resetSharedFacebookState = () => {
  FacebookBaseFacebookEvent.params = {};
  FacebookBaseFacebookEvent.eventData = {};
};

// Copies the event off the shared statics and resets them synchronously, before
// logFacebookEvent's first await. Two events logged in the same tick (a listing
// landing page fires PageView and ViewContent together once analytics are
// ready) would otherwise read — and reset — each other's params mid-flight.
const takeEvent = (eventClass) => {
  const event = {
    name: eventClass.name,
    custom_name: eventClass.custom_name,
    params: eventClass.params,
    eventData: eventClass.eventData,
    explicitEventID: eventClass._explicitEventID,
    currentUserUUID: eventClass.currentUserUUID,
    sourceUrl: eventClass._sourceUrl,
    deferUntilConsent: !!eventClass.deferUntilConsent,
  };
  eventClass._explicitEventID = false;
  eventClass._sourceUrl = null;
  // withParams()/withEventID() assign own statics on the subclass, which the
  // base reset below would not clear: drop them so the next event of this
  // class starts from the shared (empty) objects again.
  delete eventClass.params;
  delete eventClass.eventData;
  resetSharedFacebookState();
  return event;
};

// Page events fired before the visitor answered the consent banner. A first
// visit lands, the events fire, nothing is granted yet and they would be
// dropped; they are replayed once consent is granted on that same page.
let pendingUntilConsent = [];
let listeningForConsent = false;

const deferUntilConsent = (event) => {
  pendingUntilConsent.push({ event, href: window.location.href });
  if (listeningForConsent) return;
  listeningForConsent = true;
  onConsentChange(() => {
    const pending = pendingUntilConsent;
    pendingUntilConsent = [];
    // The visitor moved on before deciding: those pages were never consented to.
    pending
      .filter(({ href }) => href === window.location.href)
      .forEach(({ event: deferred }) => sendFacebookEvent(deferred, true));
  });
};

const sendFacebookEvent = async (event, replayed = false) => {
  const [marketing, statistics] = await Promise.all([
    hasEffectiveConsent('marketing'),
    hasEffectiveConsent('statistics'),
  ]);
  // The relay drives BOTH server sinks: Meta CAPI (marketing) and the GA4
  // Measurement Protocol mirror (statistics). Fire it whenever either applies —
  // gating on marketing alone left server-side GA4 dead for statistics-only users.
  if (!marketing && !statistics) {
    if (event.deferUntilConsent && !replayed && getConsent() === null) deferUntilConsent(event);
    return;
  }

  // Meta dedups Pixel vs CAPI via event_id — it must be unique per event, not
  // per class (the old static uuid was generated once at module load).
  if (!event.explicitEventID) {
    event.eventData = { ...event.eventData, eventID: uuidv4() };
  }

  try {
    // The client-side FB Pixel is marketing-only; skip its init/track without
    // marketing consent, but still relay so the GA4 mirror can fire.
    if (marketing) {
      const ReactPixel = await import('@bettercart/react-facebook-pixel');

      // A first-visit replay runs off the same consent change as applyConsent,
      // which boots the pixel too — and usually loses that race.
      if (!isFacebookPixelInitialized()) initFacebookPixel(ReactPixel.default);

      if (event.name === 'PageView') {
        // Not pageView(): that is fbq('track', 'PageView') without the eventID
        // Meta dedupes the relayed CAPI copy by, and fbevents.js drops every
        // tracked PageView after the first one per document. trackSingle is
        // exempt from that guard.
        ReactPixel.default.trackSingle(config.facebook.pixel.id || '', 'PageView', event.params, event.eventData);
      } else if (event.name === 'Custom') {
        ReactPixel.default.trackCustom(event.custom_name, event.params, event.eventData);
        event.name = event.custom_name;
      } else {
        ReactPixel.default.track(event.name, event.params, event.eventData);
      }
    }

    // CompleteRegistration is Pixel-only by design — not relayed server-side
    // (the server sign_up mirror would double-count the client registration).
    // The server would still mirror this as sign_up (FB_TO_GA4_EVENT), and
    // this return also skips Meta CAPI and the identity sync — keep it.
    if (event.name === 'CompleteRegistration') return;

    // Awaited: rejections must land in the catch below, not escape as an
    // unhandled promise rejection (logEvent's contract is a fully-awaited chain).
    await eventTracking({
      userId: event.currentUserUUID,
      event: { name: event.name, params: event.params, eventData: event.eventData },
      url: event.sourceUrl || window?.location?.href,
      consent: { statistics, marketing },
      ids: collectTrackingIds(),
      attribution: getAttribution(),
    });
  } catch (e) {
    logError(e, 'facebook-event-tracking');
  }
};

const logFacebookEvent = async (eventClass) => {
  const event = takeEvent(eventClass);
  if (typeof window === 'undefined') return;
  await sendFacebookEvent(event);
};

export const setFacebookUser = (currentUser) => {
  if (typeof window === 'undefined') return;

  hasEffectiveConsent('marketing')
    .then((permission) => {
      if (!permission) return;
      return import('@bettercart/react-facebook-pixel');
    })
    .then((ReactPixel) => {
      if (!ReactPixel) return;
      initFacebookPixel(ReactPixel.default, {
        ct: '',
        country: '',
        db: '',
        em: currentUser.attributes.email,
        fn: currentUser.attributes.profile.firstName,
        ge: '',
        ln: currentUser.attributes.profile.lastName,
        ph: currentUser.attributes.profile.protectedData.phoneNumber,
        st: '',
        zp: '',
      });
    });
};
